import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/ui/data-table';
import { cn } from '@/lib/utils';
import type { AuditEventRow } from '@services/activity.service';
import { formatActivityDateTime } from '../formatActivityDateTime';

const RESULT_TONE: Record<string, string> = {
  success: 'text-success',
  failure: 'text-warning',
};

interface AuditEventsTableProps {
  rows: AuditEventRow[];
  loading?: boolean;
}

const AuditEventsTable: React.FC<AuditEventsTableProps> = ({ rows, loading }) => {
  const { t } = useTranslation();

  const columns = useMemo<ColumnDef<AuditEventRow, any>[]>(
    () => [
      {
        id: 'time',
        accessorKey: 'created_at',
        header: t('audit.time'),
        cell: ({ row }) => (
          <span className="revy-figure whitespace-nowrap">
            {formatActivityDateTime(row.original.created_at)}
          </span>
        ),
      },
      {
        id: 'action',
        accessorKey: 'action',
        header: t('audit.action'),
        cell: ({ row }) => <code>{row.original.action}</code>,
      },
      {
        id: 'result',
        accessorKey: 'result',
        header: t('audit.result'),
        cell: ({ row }) => (
          <span className={cn('text-xs', RESULT_TONE[row.original.result] ?? 'text-destructive')}>
            {row.original.result}
          </span>
        ),
      },
      {
        id: 'user',
        accessorKey: 'user_id',
        header: t('audit.user'),
        cell: ({ row }) => (
          <span className="revy-figure">{row.original.user_id?.slice(0, 8) ?? '-'}</span>
        ),
      },
      {
        id: 'resource',
        accessorKey: 'resource_type',
        header: t('audit.resource'),
        cell: ({ row }) => (
          <span className="revy-cjk">
            {row.original.resource_type
              ? `${row.original.resource_type}${row.original.resource_name ? `: ${row.original.resource_name}` : ''}`
              : '-'}
          </span>
        ),
      },
      {
        id: 'duration',
        accessorKey: 'duration_ms',
        header: t('audit.durationMs'),
        cell: ({ row }) => <span className="revy-figure">{row.original.duration_ms ?? '-'}</span>,
      },
      {
        id: 'ip',
        accessorKey: 'ip_address',
        header: 'IP',
        cell: ({ row }) => <span className="revy-figure">{row.original.ip_address ?? '-'}</span>,
      },
    ],
    [t],
  );

  return (
    <DataTable
      paginated
      columns={columns}
      data={rows}
      loading={loading}
      loadingLabel={t('common.loading')}
      getRowId={(row) => row.id}
      empty={
        <span className="revy-cjk text-sm leading-[1.6] text-muted-foreground">
          {t('audit.noMatchingEvents')}
        </span>
      }
    />
  );
};

export default AuditEventsTable;
